import { useRefinementList, UseRefinementListProps, useInstantSearch } from 'react-instantsearch';
import { ChangeEvent, useCallback, useRef, useEffect } from 'react';
import { IconResolver } from '../IconResolver';

type RefinementListSelectProps = UseRefinementListProps & { label: string };

export function AlgoliaSelector({
	label,
	index = 'newsearch',
	removeCannotRefine = true,
	setFiltersStates,
	...props
}: RefinementListSelectProps & { index?: 'newsearch' | 'updateddata'; removeCannotRefine?: boolean; setFiltersStates?: Function }) {
	// Don't add filters. prefix to top-level fields like articleType
	const attribute = props.attribute;
	const { items, refine, canRefine } = useRefinementList({ ...props, attribute });
	const { uiState, setUiState } = useInstantSearch();

	const selectedItem = items.find(item => item.isRefined);
	const selectedValue = selectedItem ? selectedItem.value : 'all';

	const state = useRef<{ canRefine: boolean | undefined }>({
		canRefine: undefined,
	}).current;

	// Move state updates to useEffect
	useEffect(() => {
		if (canRefine !== state.canRefine && setFiltersStates) {
			state.canRefine = canRefine;
			const filterName = props.attribute.replace('filters.', '');
			setFiltersStates(prev => ({ ...prev, [filterName]: true }));
		}
	}, [canRefine, state.canRefine, setFiltersStates, props.attribute]);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>) => {
			const value = event.target.value;
			if (value === 'all') {
				if (selectedValue !== 'all') {
					const refinementList = { ...(uiState[index].refinementList || {}) };
					delete refinementList[attribute];

					setUiState({
						...uiState,
						[index]: {
							...uiState[index],
							refinementList,
						},
					});
				}
			} else {
				setUiState({
					...uiState,
					[index]: {
						...uiState[index],
						refinementList: {
							...(uiState[index]?.refinementList || {}),
							[attribute]: [value],
						},
					},
				});
			}
		},
		[uiState, selectedValue, attribute, index, setUiState],
	);

	// Don't hide the filter if it's the data index
	if (index === 'updateddata') {
		return (
			<div className='relative text-text-secondary w-full'>
				<select
					className='py-3 pl-4 pr-[2.125rem] appearance-none disabled:bg-border-secondary border border-border-secondary rounded-[0.5rem] w-full'
					id={`algolia-selector-${attribute}`}
					value={selectedValue}
					onChange={handleChange}
					aria-label={`Filter by ${label}`}
				>
					<option value='all'>{label}</option>
					{items.map(item => (
						<option key={item.value} value={item.value}>
							{item.label} ({item.count})
						</option>
					))}
				</select>
				<span className='absolute right-4 pointer-events-none top-1/2 -translate-y-1/2 h-4 w-4 min-w-4 mt-auto'>
					<IconResolver icon={'caret-down'} className='w-[1.25rem] h-[1.25rem]' />
				</span>
			</div>
		);
	}

	// For other indexes, maintain the original visibility logic
	return removeCannotRefine && !canRefine ? null : (
		<div className='relative text-text-secondary'>
			<select
				className='py-3 pl-4 pr-[2.125rem] appearance-none disabled:bg-border-secondary border border-border-secondary rounded-[0.5rem] w-full'
				id={`algolia-selector-${attribute}`}
				value={selectedValue}
				disabled={!canRefine}
				onChange={handleChange}
			>
				<option value='all'>{label}</option>
				{items.map(item => (
					<option key={item.value} value={item.value}>
						{item.label} ({item.count})
					</option>
				))}
			</select>
			<span className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 min-w-4 mt-auto'>
				<IconResolver icon={'caret-down'} className='w-[1.25rem] h-[1.25rem]' />
			</span>
		</div>
	);
}
